import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { WorkspaceProvider, useWorkspaceQuery } from '@/lib/workspace';
import { ErrorState, LoadingPage } from './states';
import { AppShell } from './app-shell';

/** Signed-in users only; remembers where they were heading. */
export function RequireAuth() {
  const { loading, session } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingPage />;
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

/**
 * Loads the workspace and the caller's tool surface, then renders the app shell.
 * A signed-in user with no workspace goes to onboarding.
 */
export function RequireWorkspace() {
  const { needsWorkspace } = useAuth();
  const query = useWorkspaceQuery();

  if (needsWorkspace) return <Navigate to="/onboarding" replace />;
  if (query.isPending) return <LoadingPage label="Opening workspace" />;
  if (query.error) {
    // No membership yet reads as a permission problem; send them to onboarding.
    if (query.error.status === 403 || query.error.code === 'permission_denied') return <Navigate to="/onboarding" replace />;
    return (
      <div className="p-8">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  return (
    <WorkspaceProvider data={query.data}>
      <AppShell>
        <Outlet />
      </AppShell>
    </WorkspaceProvider>
  );
}
